import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';

const ClassTracker = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const student = location.state?.student;

  const [attendance, setAttendance] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(() => {
    if (!student) {
      navigate('/student-login');
      return;
    }
    loadTrackerData();
  }, [student, navigate]);

  const loadTrackerData = async () => {
    try {
      const [attendanceRes, schedulesRes, feedbackRes] = await Promise.all([
        fetch(`${API_ENDPOINTS.BASE_URL}/api/attendance/${student.username}`),
        fetch(`${API_ENDPOINTS.BASE_URL}/api/schedules/${student.className}`),
        fetch(`${API_ENDPOINTS.BASE_URL}/api/feedback/${student.username}`)
      ]);

      const attendanceData = await attendanceRes.json();
      const schedulesData = await schedulesRes.json();
      const feedbackData = await feedbackRes.json();

      setAttendance(attendanceData);
      setSchedules(schedulesData);
      setFeedback(feedbackData);
    } catch (error) {
      console.error('Error loading tracker data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAttendanceStats = () => {
    const present = attendance.filter(a => a.status === 'present').length;
    const absent = attendance.filter(a => a.status === 'absent').length;
    const late = attendance.filter(a => a.status === 'late').length;
    const total = attendance.length;

    return { present, absent, late, total };
  };

  const getAttendancePercentage = () => {
    const { present, total } = getAttendanceStats();
    return total > 0 ? Math.round((present / total) * 100) : 0;
  };

  const getFilteredAttendance = () => {
    return attendance.filter(record => {
      const recordDate = new Date(record.date);
      return recordDate.getMonth() === selectedMonth && recordDate.getFullYear() === selectedYear;
    });
  };

  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const generateCalendarDays = () => {
    const daysInMonth = getDaysInMonth(selectedMonth, selectedYear);
    const days = [];

    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const attendanceRecord = attendance.find(a => a.date === dateStr);
      days.push({
        day,
        date: dateStr,
        status: attendanceRecord?.status || 'no-record'
      });
    }

    return days;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'present': return 'bg-green-500';
      case 'absent': return 'bg-red-500';
      case 'late': return 'bg-yellow-500';
      default: return 'bg-gray-300';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'present': return 'Present';
      case 'absent': return 'Absent';
      case 'late': return 'Late';
      default: return 'No Record';
    }
  };

  if (!student) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-blue-50 flex items-center justify-center">
        <div className="text-xl text-blue-600">Loading class tracker...</div>
      </div>
    );
  }

  const { present, absent, late, total } = getAttendanceStats();
  const attendancePercentage = getAttendancePercentage();
  const calendarDays = generateCalendarDays();

  return (
    <div className="min-h-screen bg-blue-50 p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-blue-900">Class Tracker</h1>
          <button
            onClick={() => navigate('/student-dashboard', { state: { student } })}
            className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
          >
            Back to Dashboard
          </button>
        </div>

        {/* Attendance Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6 text-center">
            <div className={`w-16 h-16 rounded-full mx-auto mb-3 flex items-center justify-center text-white text-2xl font-bold ${attendancePercentage >= 90 ? 'bg-green-500' : attendancePercentage >= 75 ? 'bg-yellow-500' : 'bg-red-500'}`}>
              {attendancePercentage}%
            </div>
            <h3 className="text-lg font-semibold text-gray-800">Attendance Rate</h3>
            <p className="text-sm text-gray-600">{present}/{total} days</p>
          </div>

          <div className="bg-green-50 rounded-lg shadow p-6 text-center">
            <div className="text-3xl mb-2">Loading</div>
            <h3 className="text-lg font-semibold text-green-800">{present}</h3>
            <p className="text-sm text-green-600">Present</p>
          </div>

          <div className="bg-red-50 rounded-lg shadow p-6 text-center">
            <div className="text-3xl mb-2">X</div>
            <h3 className="text-lg font-semibold text-red-800">{absent}</h3>
            <p className="text-sm text-red-600">Absent</p>
          </div>

          <div className="bg-yellow-50 rounded-lg shadow p-6 text-center">
            <div className="text-3xl mb-2">Time</div>
            <h3 className="text-lg font-semibold text-yellow-800">{late}</h3>
            <p className="text-sm text-yellow-600">Late</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Attendance Calendar */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Attendance Calendar</h2>
              <div className="flex space-x-2">
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                  className="border rounded px-3 py-1 text-sm"
                >
                  {Array.from({ length: 12 }, (_, i) => (
                    <option key={i} value={i}>
                      {new Date(0, i).toLocaleString('default', { month: 'long' })}
                    </option>
                  ))}
                </select>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="border rounded px-3 py-1 text-sm"
                >
                  {[2024, 2025, 2026].map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-7 gap-1 mb-4">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center text-sm font-semibold text-gray-600 py-2">
                  {day}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((dayInfo, index) => (
                <div
                  key={index}
                  className={`aspect-square rounded-lg flex items-center justify-center text-sm font-semibold cursor-pointer hover:opacity-80 transition-opacity ${getStatusColor(dayInfo.status)} ${dayInfo.status === 'no-record' ? 'text-gray-600' : 'text-white'}`}
                  title={`${dayInfo.day}: ${getStatusText(dayInfo.status)}`}
                >
                  {dayInfo.day}
                </div>
              ))}
            </div>

            {/* Legend */}
            <div className="mt-6 flex flex-wrap gap-4 text-sm">
              <div className="flex items-center">
                <div className="w-4 h-4 bg-green-500 rounded mr-2"></div>
                <span>Present</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-red-500 rounded mr-2"></div>
                <span>Absent</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-yellow-500 rounded mr-2"></div>
                <span>Late</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-gray-300 rounded mr-2"></div>
                <span>No Record</span>
              </div>
            </div>
          </div>

          {/* Class Schedule & Feedback */}
          <div className="space-y-6">
            {/* Class Schedule */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Today's Schedule</h2>
              {schedules.length === 0 ? (
                <p className="text-gray-600">No schedule available for your class.</p>
              ) : (
                <div className="space-y-3">
                  {schedules.slice(0, 5).map((schedule) => (
                    <div key={schedule.id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <h3 className="font-bold text-gray-800">{schedule.subject}</h3>
                          <p className="text-sm text-gray-600">{schedule.day} at {schedule.time}</p>
                        </div>
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded text-sm">
                          {schedule.room || 'TBD'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Teacher Feedback */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Recent Feedback</h2>
              {feedback.length === 0 ? (
                <p className="text-gray-600">No feedback received yet.</p>
              ) : (
                <div className="space-y-3">
                  {feedback.slice(0, 3).map((item) => (
                    <div key={item.id} className="border rounded-lg p-4 bg-blue-50">
                      <div className="flex items-start space-x-3">
                        <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold">
                          {item.teacherUsername?.charAt(0).toUpperCase() || 'T'}
                        </div>
                        <div className="flex-1">
                          <div className="flex justify-between items-center mb-2">
                            <h4 className="font-semibold text-gray-800">
                              {item.teacherUsername || 'Teacher'}
                            </h4>
                            <span className="text-sm text-gray-500">
                              {new Date(item.date).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="text-gray-700 text-sm">{item.message}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClassTracker;



